import {
  DeleteOutlined,
  FlagOutlined,
  PlusOutlined,
  RightOutlined,
  WarningFilled
} from "@ant-design/icons"
import { Button, Flex, Space, Tag, Tooltip, Typography } from "antd"
import type { ItemType } from "antd/es/menu/interface"
import { type ColumnsType } from "antd/es/table"
import dayjs from "dayjs"
import React from "react"

import Icon from "../../../../../../../Icon"
import { empty } from "../../../../../../../constants/placeholders"
import { tableGuideAnchor } from "../../../../../../../utils/table-guide-anchor.ts"
import { budgetFormat } from "../../../../../../../utils/number/budget-format.ts"
import {
  type Attachment,
  AttachmentModels
} from "../../../../../../Album/types/attachment.types.ts"
import { ALL_DATE_FORMATS } from "../../../../../../TeamManagementPage/constants/date-formats.ts"
import { AttachmentsController } from "../../../../../components/AttachmentsController.tsx"
import { RowActionsMenu } from "../../../../../../../components/EntityTable/RowActionsMenu.tsx"
import type { FinancialTransactionItem } from "../../../../../types/project-finance.types.tsx"
import {
  ENTRY_TYPE_ICONS,
  ENTRY_TYPE_LABELS,
  FINANCE_ITEM_STATUSES,
  FINANCE_ITEM_STATUSES_MAP,
  FINANCE_ITEM_TYPES,
  FINANCE_ITEM_TYPES_MAP,
  isFinanceItemLocked,
  type EntryType,
  type FinanceItemStatuses
} from "../../../../constants/finance-item-constants.ts"
import { PaymentsSummary } from "./PaymentsSummary.tsx"

const { Text, Paragraph } = Typography

type Translate = (key: string, options?: Record<string, unknown>) => string

interface GetColumnsColumnsArgs {
  t: Translate
  currentUserId: string
  isLocked: boolean
  projectId: string
  userDateFormat: string
  //
  isAdding?: boolean
  editAllowed?: boolean
  deleteAllowed?: boolean
  uploadFileAllowed?: boolean
  deleteFileAllowed?: boolean
  deleteForeignFileAllowed?: boolean
  generateInvoiceAllowed?: boolean
  //
  onSelectRow: (record: FinancialTransactionItem, action: string) => void
  onCompletePayment: (record: FinancialTransactionItem) => void
  onOpenPayments: (record: FinancialTransactionItem) => void
}

export const getColumns = ({
  t,
  currentUserId,
  projectId,
  isLocked,
  userDateFormat,
  isAdding,
  editAllowed,
  deleteAllowed,
  deleteForeignFileAllowed,
  uploadFileAllowed,
  deleteFileAllowed,
  generateInvoiceAllowed,
  onSelectRow,
  onCompletePayment,
  onOpenPayments
}: GetColumnsColumnsArgs): ColumnsType<FinancialTransactionItem> => [
  {
    title: t("finance.columns.paymentDate"),
    dataIndex: "date",
    key: "date",
    sorter: true,
    width: 135,
    render: (value: string, record) => {
      const formats = userDateFormat || ALL_DATE_FORMATS
      const parsedDate = dayjs(record?.date, formats, true)
      const parsedDue = dayjs(record?.due_date, formats, true)
      const backDated =
        parsedDate.isValid() &&
        parsedDue.isValid() &&
        parsedDate.isAfter(parsedDue, "day")
      return (
        <Space>
          <Text>{value || empty}</Text>
          {backDated && (
            <Tooltip title={t("finance.tooltips.dateAfterDueDate")}>
              <WarningFilled className="text-warning" />
            </Tooltip>
          )}
        </Space>
      )
    }
  },
  {
    title: t("finance.columns.financialItem"),
    dataIndex: "name",
    key: "name",
    width: 180,
    render: (text: string) => <Text ellipsis={{ tooltip: text }}>{text}</Text>
  },
  {
    title: t("finance.columns.type"),
    dataIndex: "item_type_display",
    key: "item_type_display",
    width: 135,
    render: (type: string, record) => {
      const config = FINANCE_ITEM_TYPES_MAP?.[record?.item_type]

      return (
        <Tag style={config?.style} className="m-0">
          {type}
        </Tag>
      )
    }
  },
  {
    title: t("finance.columns.entryType"),
    dataIndex: "entry_type",
    key: "entry_type",
    width: 160,
    render: (value: EntryType, record) => {
      const label =
        record?.entry_type_display || ENTRY_TYPE_LABELS[value] || value
      const IconComponent = value ? (ENTRY_TYPE_ICONS[value] ?? null) : null

      return (
        <Space size={4}>
          {IconComponent && <IconComponent />}
          <Text>{label || empty}</Text>
        </Space>
      )
    }
  },
  {
    title: t("finance.columns.dueDate"),
    dataIndex: "due_date",
    key: "due_date",
    sorter: true,
    width: 135,
    render: (value: string) => {
      const parsedDate = dayjs(value, userDateFormat || ALL_DATE_FORMATS, true)
      const isOverdue = parsedDate && parsedDate.isBefore(dayjs().tz(), "day")

      return (
        <Space>
          <Text>{value || empty}</Text>
          {isOverdue && (
            <Tooltip title={t("finance.tooltips.overdue")}>
              <WarningFilled className={"text-warning"} />
            </Tooltip>
          )}
        </Space>
      )
    }
  },
  {
    title: t("finance.columns.comments"),
    dataIndex: "comment",
    key: "comment",
    width: 200,
    onCell: () => ({ style: { maxWidth: 200 } }),
    render: (description: string) => (
      <Paragraph
        className={"mb-0"}
        ellipsis={{ rows: 3, tooltip: description }}
      >
        {description || empty}
      </Paragraph>
    )
  },
  {
    title: <span data-guide-target="finance-col-value">{t("finance.columns.committedValueUsd")}</span>,
    dataIndex: "value",
    key: "value",
    width: 145,
    render: (value: number | string, record: FinancialTransactionItem) => {
      const planned = typeof value === "string" ? parseFloat(value) : value
      const paid = parseFloat(record.exercised_value ?? "0")
      const hasPayments = (record.partial_finances?.length ?? 0) > 0
      if (hasPayments && Number.isFinite(paid) && paid !== 0) {
        if (record.is_overpaid) {
          return (
            <Tooltip
              title={t("finance.tooltips.overpaidBy", {
                amount: budgetFormat(paid - planned)
              })}
            >
              <Text className="text-red-500 font-bold">
                {`${budgetFormat(paid)} / ${budgetFormat(planned)}`}
              </Text>
            </Tooltip>
          )
        }
        return <Text>{`${budgetFormat(paid)} / ${budgetFormat(planned)}`}</Text>
      }
      return <Text>{budgetFormat(planned)}</Text>
    }
  },
  {
    title: (
      <span data-guide-target="finance-col-source-target">
        {t("finance.columns.sourceTarget")}
      </span>
    ),
    dataIndex: "responsible_type_display",
    key: "responsible_type_display",
    width: 150,
    render: (value: string) => value || empty
  },
  {
    //todo ????
    title: t("finance.columns.name"),
    dataIndex: "custom_subcontractor_name",
    key: "custom_subcontractor_name",
    width: 250,
    ellipsis: true,
    render: (customName, record) => {
      const name =
        customName || record?.subcontractor_name || record?.assignee_name

      if (!name && record?.subcontractor) {
        return t("finance.cells.unnamed")
      }

      return name || empty
    }
  },
  {
    title: t("finance.columns.projectType"),
    dataIndex: "project_type_display",
    key: "project_type_display",
    width: 200,
    render: (value: string | null) => value || empty
  },
  {
    title: (
      <span data-guide-target="finance-col-milestone">{t("finance.columns.milestone")}</span>
    ),
    // WP3.11: sortable by milestone order. dataIndex drives the ?ordering field
    // (project_phase__order) sent to the backend; the cell still shows the name.
    dataIndex: "project_phase__order",
    key: "project_phase_name",
    width: 200,
    ellipsis: true,
    sorter: true,
    render: (_, record) => (
      <Text ellipsis={{ tooltip: record?.project_phase_name }}>
        {record?.project_phase_name || empty}
      </Text>
    )
  },
  {
    title: t("finance.columns.invoice"),
    dataIndex: "invoice_display",
    key: "invoice_display",
    width: 200,
    render: (value: string | null) => value || empty
  },
  {
    title: t("finance.columns.paymentMethod"),
    dataIndex: "payment_method_display",
    key: "payment_method_display",
    width: 150,
    render: (value: string | null) => value || empty
  },
  {
    title: t("finance.columns.paymentDetails"),
    dataIndex: "payment_details",
    key: "payment_details",
    width: 180,
    render: (value: string | null) => value || empty
  },
  {
    title: t("finance.columns.status"),
    dataIndex: "status",
    key: "status",
    width: 140,
    fixed: "right",
    onHeaderCell: () => tableGuideAnchor("finance-col-status"),
    render: (status: FinanceItemStatuses, record) => {
      const statusData = FINANCE_ITEM_STATUSES_MAP?.[status]

      return (
        <Tag style={statusData?.style} className="m-0">
          {record?.status_display}
          {record?.is_planned && status === FINANCE_ITEM_STATUSES.PLANNED && (
            <Tooltip title={t("finance.tooltips.initiallyPlanned")}>
              <FlagOutlined className="ml-1 text-xs " />
            </Tooltip>
          )}
        </Tag>
      )
    }
  },
  {
    title: t("finance.columns.payments"),
    key: "payments",
    dataIndex: "partial_finances",
    width: 150,
    fixed: "right",
    render: (_: unknown, record) => {
      const count = record.partial_finances?.length ?? 0

      if (count === 0) {
        // Recording a payment is a write on the line, so it answers to the
        // same right as the row's own edit, which an archive has cleared.
        if (!editAllowed || isFinanceItemLocked(record?.status)) {
          return <Text type="secondary">-</Text>
        }
        return (
          <Button
            type="link"
            size="small"
            className="px-0"
            icon={<PlusOutlined />}
            onClick={() => onOpenPayments(record)}
          >
            {t("finance.cells.addPayment")}
          </Button>
        )
      }

      return (
        <Button
          type="link"
          size="small"
          className="h-auto px-0 text-left"
          onClick={() => onOpenPayments(record)}
        >
          <Flex vertical align="flex-start" className="leading-tight">
            <Space size={4}>
              <Text>{t("finance.cells.paymentCount", { count })}</Text>
              <RightOutlined className="text-xs" />
            </Space>
            <PaymentsSummary record={record} compact />
          </Flex>
        </Button>
      )
    }
  },
  {
    title: t("finance.columns.attachments"),
    key: "attachments",
    dataIndex: "attachments",
    width: 100,
    align: "center",
    fixed: "right",
    render: (attachments: Attachment[], record) => (
      <AttachmentsController
        currentUserId={currentUserId}
        // Locked items keep files viewable but offer no new uploads.
        addAllowed={uploadFileAllowed && !isFinanceItemLocked(record?.status)}
        deleteAllowed={deleteFileAllowed}
        deleteForeignAllowed={deleteForeignFileAllowed}
        project_id={projectId}
        model_type={AttachmentModels.FINANCIAL_ITEM}
        object_id={record?.id}
        attachments={attachments}
      />
    )
  },
  {
    title: t("finance.columns.actions"),
    key: "actions",
    width: 90,
    align: "center",
    fixed: "right",
    hidden: !editAllowed && !deleteAllowed && !isAdding,
    render: (_, record) => {
      if (!editAllowed && !deleteAllowed) return null
      return (
        <Flex gap={"small"} justify={"center"} className={"w-20 mx-auto"}>
          <RowActionsMenu
            disabled={isLocked}
            items={
              [
                ...(editAllowed
                  ? [
                      {
                        key: "edit_row",
                        label: t("finance.actions.edit"),
                        icon: <Icon type={"Edit"} />,
                        onClick: () => onSelectRow(record, "edit_row")
                      }
                    ]
                  : []),
                ...(editAllowed &&
                record?.status !== FINANCE_ITEM_STATUSES.COMPLETED &&
                record?.status !== FINANCE_ITEM_STATUSES.CANCELLED
                  ? [
                      {
                        key: "complete_payment",
                        label: t("finance.actions.completePayment"),
                        icon: <FlagOutlined />,
                        onClick: () => onCompletePayment(record)
                      }
                    ]
                  : []),
                ...(generateInvoiceAllowed &&
                record?.item_type === FINANCE_ITEM_TYPES.RECEIVABLE
                  ? [
                      {
                        key: "generate_invoice",
                        label: t("finance.actions.generateInvoice"),
                        icon: <Icon type={"Dollar"} />,
                        onClick: () => onSelectRow(record, "generate_invoice")
                      }
                    ]
                  : []),
                ...(deleteAllowed
                  ? [
                      {
                        key: "delete_row",
                        label: t("finance.actions.delete"),
                        icon: <DeleteOutlined />,
                        danger: true,
                        onClick: () => onSelectRow(record, "delete_row")
                      }
                    ]
                  : [])
              ] as ItemType[]
            }
          />
        </Flex>
      )
    }
  }
]
